import { Typography } from 'antd';
import { StyledStack } from 'app/components/Stack';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import RangeDatePickerCustom from 'app/pages/NewAccount/CheckTickets/RangeDatePickerCustom';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  createDateObjectByTimezone,
  FilterFormValues,
  TYPE_REPORT,
} from './Filter';
import useTitleSize from 'app/components/StyledTable/useTitleSize';
import moment, { Moment } from 'moment-timezone';
import { useForm, useFormContext } from 'react-hook-form';
import StyledTabs from 'app/components/StyledTabs';
import { RangeDatePickerContainer, SmallTitle } from './OverviewScreen';
import { ReactComponent as ArrowDown } from 'app/assets/img/arrow_down.svg';
import { PageStateContext, SCREEN } from '..';
import { ReactComponent as ExportExcel } from 'app/assets/img/export-excel-icon.svg';
import useWindowSize from 'app/hooks/useWindowSize';
import StyledTable from 'app/components/StyledTable';
import styled from 'styled-components';
import useFetchData from 'app/hooks/useFetchData';
import {
  exportTransactionCSV,
  getTransactionHistoryApi,
} from 'app/services/agentApi';
import { formatNumber } from 'utils/helper';
import StyledPagination from 'app/components/StyledPagination';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { AxiosError } from 'axios';
import { useHistory } from 'react-router-dom';
import * as _ from 'lodash';

enum TABS {
  DEBIT_INCURRED = 'N',
  CREDIT_INCURRED = 'C',
}

type TabFilter = {
  debitCreditIndicator: TABS;
  dateFrom?: Moment | null;
  dateTo?: Moment | null;
  page: number;
  limit: number;
  typeReport: TYPE_REPORT;
};

export const transformFilterValues = (rawData: TabFilter) => {
  return {
    debitCreditIndicator: rawData.debitCreditIndicator,
    dateFrom: rawData.dateFrom?.format('DD/MM/YYYY'),
    dateTo: rawData.dateTo?.format('DD/MM/YYYY'),
    page: rawData.page,
    limit: rawData.limit,
    typeReport: rawData.typeReport,
  };
};

const DetailScreen: React.FC = () => {
  const translations = useSelector(selectTranslations);
  const [activeTab, setActiveTab] = useState(TABS.DEBIT_INCURRED);
  const history = useHistory();

  const { setScreen } = useContext(PageStateContext);

  const titleColumnSizes = useTitleSize({
    title: [
      'b2b.txt.agent-table-order',
      'b2b.txt.time',
      'b2b.txt.order-id',
      'b2b.txt.order-code',
      'b2b.txt.root-order',
      'b2b.txt.transaction-type',
      'b2b.txt.transaction_name',
      'b2c.txt.pttt',
      'b2b.txt.payment-method-root',
      'b2b.txt.amount-payment',
      'b2b.txt.penaltyFee',
      'b2b.txt.productType',
    ],
  });

  const columns = useMemo(
    () => [
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.agent-table-order']}
          </div>
        ),
        key: 'order',
        dataIndex: 'order',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'left',
                minWidth: titleColumnSizes['b2b.txt.agent-table-order'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.time']}
          </div>
        ),
        dataIndex: 'created_at',
        key: 'created_at',
        render: value => {
          return (
            <StyledStack
              justifyContent={'start'}
              style={{ minWidth: titleColumnSizes['b2b.txt.time'] }}
            >
              <div style={{ textAlign: 'left' }}>
                <Typography>{value}</Typography>
              </div>
            </StyledStack>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.order-id']}
          </div>
        ),
        dataIndex: 'documentNumber',
        key: 'documentNumber',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.order-id'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.order-code']}
          </div>
        ),
        dataIndex: 'orderCode',
        key: 'orderCode',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.order-code'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.root-order']}
          </div>
        ),
        dataIndex: 'rootOrder',
        key: 'rootOrder',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.root-order'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.transaction-type']}
          </div>
        ),
        dataIndex: 'transactionType',
        key: 'transactionType',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.transaction-type'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.transaction_name']}
          </div>
        ),
        dataIndex: 'transactionName',
        key: 'transactionName',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.transaction_name'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2c.txt.pttt']}
          </div>
        ),
        dataIndex: 'pttt',
        key: 'pttt',
        render: value => (
          <div
            style={{
              textAlign: 'center',
              minWidth: titleColumnSizes['b2c.txt.pttt'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.payment-method-root']}
          </div>
        ),
        dataIndex: 'paymentMethodRoot',
        key: 'paymentMethodRoot',
        render: value => (
          <div
            style={{
              textAlign: 'center',
              minWidth: titleColumnSizes['b2b.txt.payment-method-root'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.amount-payment']}
          </div>
        ),
        dataIndex: 'amountPayment',
        key: 'amountPayment',
        render: value => (
          <div
            style={{
              textAlign: 'right',
              minWidth: titleColumnSizes['b2b.txt.amount-payment'],
            }}
          >
            {_.isNil(value)
              ? null
              : `${formatNumber(value).replaceAll(',', '.')}đ`}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.penaltyFee']}
          </div>
        ),
        dataIndex: 'penaltyFee',
        key: 'penaltyFee',
        render: value => (
          <div
            style={{
              textAlign: 'right',
              minWidth: titleColumnSizes['b2b.txt.penaltyFee'],
            }}
          >
            {_.isNil(value)
              ? null
              : `${formatNumber(value).replaceAll(',', '.')}đ`}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.productType']}
          </div>
        ),
        dataIndex: 'productType',
        key: 'productType',
        render: (_, record) => (
          <div
            style={{
              textAlign: 'center',
              minWidth: titleColumnSizes['b2b.txt.productType'],
            }}
          >
            {
              translations?.[
                record.isHoliday
                  ? 'b2b.txt.holidayComboTicket'
                  : 'b2b.txt.parkTicket'
              ]
            }
          </div>
        ),
      },
    ],
    [titleColumnSizes, translations],
  );

  const handleBack = () => {
    setScreen(SCREEN.TRANSACTION_OVERVIEW);
    window.scrollTo(0, 0);
    history.replace('/agent/transaction-history');
  };

  return (
    <StyledStack
      rowGap={'24px'}
      direction={'column'}
      padding={{ lg: '30px', md: '30px', xs: '16px' }}
      style={{
        background: 'white',
        borderRadius: 20,
        boxShadow: '0px 20px 20px 0px #0000000D',
        width: '100%',
      }}
    >
      <div className="d-flex align-items-center">
        <ArrowDown
          style={{
            cursor: 'pointer',
            height: 18,
            width: 18,
            transform: 'rotate(90deg)',
            marginRight: 5,
          }}
          onClick={handleBack}
        />
        <Typography
          style={{
            fontWeight: 600,
            fontSize: 20,
            color: 'black',
            textTransform: 'uppercase',
          }}
        >
          {translations?.['b2b.txt.transactionDetail'] ?? ''}
        </Typography>
      </div>
      <div style={{ width: '100%' }}>
        <StyledTabs
          activeKey={activeTab}
          onChange={tabKey => setActiveTab(tabKey as TABS)}
        >
          <StyledTabs.TabPane
            key={TABS.DEBIT_INCURRED}
            tabKey={TABS.DEBIT_INCURRED}
            tab={translations?.['b2b.txt.debitIncurred'] ?? ''}
          >
            <DebitIncurred columns={columns} />
          </StyledTabs.TabPane>
          <StyledTabs.TabPane
            key={TABS.CREDIT_INCURRED}
            tabKey={TABS.CREDIT_INCURRED}
            tab={translations?.['b2b.txt.creditIncurred'] ?? ''}
          >
            <CreditIncurred columns={columns} />
          </StyledTabs.TabPane>
        </StyledTabs>
      </div>
    </StyledStack>
  );
};

export default DetailScreen;

const DebitIncurred: React.FC<{ columns: Record<string, any>[] }> = ({
  columns,
}) => {
  const translations = useSelector(selectTranslations);
  const { windowSize } = useWindowSize();
  const { setPageLoading } = useContext(PageStateContext);

  const { getValues: getValuesOverviewForm } =
    useFormContext<FilterFormValues>();

  const { getValues, setValue } = useForm<TabFilter>({
    defaultValues: {
      debitCreditIndicator: TABS.DEBIT_INCURRED,
      dateFrom: getValuesOverviewForm('dateFrom'),
      dateTo:
        getValuesOverviewForm('dateTo') ?? getValuesOverviewForm('dateFrom'),
      page: 1,
      limit: 20,
      typeReport: TYPE_REPORT.DETAIL,
    },
  });

  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  const filterRef = useRef<TabFilter>(getValues());

  const tableRef = useRef<HTMLDivElement>();
  const anchorPointerRef = useRef<HTMLDivElement>(null);

  const { result: debitIncurredDetail, onFetchData: onFetchDebitIncurred } =
    useFetchData({
      actionApi: getTransactionHistoryApi,
      IIFE: false,
      onBefore: () => setPageLoading(true),
      onFinally: () => setPageLoading(false),
      payloadData: {
        debitCreditIndicator: TABS.DEBIT_INCURRED,
        dateFrom: getValues('dateFrom')?.format('DD/MM/YYYY'),
        dateTo: getValues('dateTo')?.format('DD/MM/YYYY'),
        page: getValues('page'),
        limit: getValues('limit'),
        typeReport: getValues('typeReport'),
      },
    });

  const dataSource = useMemo(() => {
    const details = debitIncurredDetail?.details || [];
    const paginator = debitIncurredDetail?.paginator || {};
    return details.map((item, index) => ({
      order: (paginator?.current - 1) * paginator?.numItemsPerPage + index + 1,
      created_at: item.created_at,
      documentNumber: item?.documentNumber,
      orderCode: item?.submitNumber,
      rootOrder: item?.original_order,
      transactionType: item?.transaction_type_name,
      transactionName: item?.so_type_name,
      pttt: item?.payMethod,
      paymentMethodRoot: item?.original_payMethod,
      amountPayment: +item?.amount,
      penaltyFee: item?.penalty_amount,
      isHoliday: item?.isHoliday || false,
    }));
  }, [debitIncurredDetail?.details, debitIncurredDetail?.paginator]);

  const disabledExportExcel = !dataSource?.length;

  const handleChangeTime = () => {
    const isSameDateFrom = filterRef.current.dateFrom?.isSame(
      getValues('dateFrom'),
      'day',
    );

    const isSameDateTo = filterRef.current.dateTo?.isSame(
      getValues('dateTo') ?? getValues('dateFrom'),
      'day',
    );

    if (isSameDateFrom && isSameDateTo) return;

    setValue('page', 1);

    filterRef.current = {
      ...filterRef.current,
      dateFrom: getValues('dateFrom'),
      dateTo: getValues('dateTo') ?? getValues('dateFrom'),
      page: 1,
    };

    onFetchDebitIncurred({
      payloadData: transformFilterValues({
        ...filterRef.current,
        dateFrom: getValues('dateFrom'),
        dateTo: getValues('dateTo') ?? getValues('dateFrom'),
        page: 1,
      }),
      callback: {
        onSuccess: () => {
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  const handleChangePage = async (page: number, pageSize: number) => {
    setValue('page', page);
    setValue('limit', pageSize);

    filterRef.current = {
      ...filterRef.current,
      page,
      limit: pageSize,
    };

    onFetchDebitIncurred({
      payloadData: transformFilterValues({
        ...filterRef.current,
        page,
        limit: pageSize,
      }),
      callback: {
        onSuccess: () => {
          if (anchorPointerRef?.current) {
            const top =
              anchorPointerRef.current.getBoundingClientRect().top +
              window.scrollY -
              180;
            window.scrollTo({ top, behavior: 'smooth' });
          }
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  const handleExportExcel = async () => {
    try {
      setPageLoading(true);
      const { page, limit, ...params } = transformFilterValues(
        filterRef.current,
      );

      const res = await exportTransactionCSV(params);

      if (res?.data?.success) {
        window.location.href = res?.data?.data?.path;
      }
    } catch (error: any) {
      onOpenDialogNoti({
        type: 'ERROR',
        content:
          error instanceof AxiosError
            ? error?.response?.data?.message
            : error?.message,
        isShow: true,
        title: '',
        onCloseDialog: onCloseDialogNoti,
      });
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    onFetchDebitIncurred();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={anchorPointerRef}>
      <div className="d-flex align-items-center justify-content-between">
        <StyledRangeDatePickerContainer>
          <RangeDatePickerCustom
            placeholder={
              translations ? translations['b2b.txt.ticket-expried'] : ''
            }
            startDateInit={createDateObjectByTimezone(getValues('dateFrom'))}
            endDateInit={createDateObjectByTimezone(getValues('dateTo'))}
            onChange={([start, end]) => {
              setValue('dateFrom', start ? moment(start) : null);
              setValue('dateTo', end ? moment(end) : null);
            }}
            minDate={null}
            onCalendarClose={handleChangeTime}
          />
        </StyledRangeDatePickerContainer>

        <Button
          onClick={handleExportExcel}
          disabled={disabledExportExcel}
          style={{
            filter: `grayscale(${disabledExportExcel ? '1' : '0'})`,
            cursor: disabledExportExcel ? 'not-allowed' : 'pointer',
          }}
        >
          <ExportExcel />
          {windowSize.width >= 1280 && (
            <SmallTitle
              style={{
                margin: 0,
                color: '#fab317',
                fontSize: 14,
                width: 'max-content',
                marginLeft: 6,
                fontWeight: 400,
              }}
            >
              {translations?.['b2b.txt.export-excel']}
            </SmallTitle>
          )}
        </Button>
      </div>

      <div style={{ width: '100%', marginTop: 24 }}>
        <StyledTable
          tableRef={tableRef}
          dataSource={dataSource ?? []}
          columns={columns}
          tableProps={{
            pagination: false,
            scroll: {
              x: dataSource?.length ? 'auto' : 'max-content',
              y: dataSource?.length ? '50vh' : undefined,
            },
          }}
          styleSlots={{
            antTableContainer: {
              overflow: 'hidden',
            },
            antTableHeader: {
              border: 'none !important',
              borderRadius: '0 !important',
            },
            tableCSS: {
              borderTopLeftRadius: !dataSource?.length ? '' : '0 !important',
              borderTopRightRadius: !dataSource?.length ? '' : '0 !important',
            },
            antTableBody: {
              paddingBottom: 10,
            },
            antTableExpandedRowFixed: {
              minHeight: 110,
            },
          }}
        />
        {debitIncurredDetail?.paginator && (
          <>
            <StyledPagination
              showSizeChanger
              style={{ margin: 'auto', marginTop: 24 }}
              current={getValues('page') || 1}
              total={debitIncurredDetail?.paginator?.totalCount || 0}
              pageSize={getValues('limit') || 20}
              onChange={handleChangePage}
              pageSizeOptions={['20', '50', '100']}
            />
          </>
        )}
      </div>
    </div>
  );
};

const CreditIncurred: React.FC<{ columns: Record<string, any>[] }> = ({
  columns,
}) => {
  const translations = useSelector(selectTranslations);
  const { windowSize } = useWindowSize();
  const { setPageLoading } = useContext(PageStateContext);

  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  const { getValues: getValuesOverviewForm } =
    useFormContext<FilterFormValues>();

  const { getValues, setValue } = useForm<TabFilter>({
    defaultValues: {
      debitCreditIndicator: TABS.CREDIT_INCURRED,
      dateFrom: getValuesOverviewForm('dateFrom'),
      dateTo:
        getValuesOverviewForm('dateTo') ?? getValuesOverviewForm('dateFrom'),
      page: 1,
      limit: 20,
      typeReport: TYPE_REPORT.DETAIL,
    },
  });

  const filterRef = useRef<TabFilter>(getValues());

  const tableRef = useRef<HTMLDivElement>();
  const anchorPointerRef = useRef<HTMLDivElement>(null);

  const { result: creditIncurredDetail, onFetchData: onFetchCreditIncurred } =
    useFetchData({
      actionApi: getTransactionHistoryApi,
      IIFE: false,
      onBefore: () => setPageLoading(true),
      onFinally: () => setPageLoading(false),
      payloadData: {
        debitCreditIndicator: TABS.CREDIT_INCURRED,
        dateFrom: getValues('dateFrom')?.format('DD/MM/YYYY'),
        dateTo: getValues('dateTo')?.format('DD/MM/YYYY'),
        page: getValues('page'),
        limit: getValues('limit'),
        typeReport: getValues('typeReport'),
      },
    });

  const dataSource = useMemo(() => {
    const details = creditIncurredDetail?.details || [];
    const paginator = creditIncurredDetail?.paginator || {};
    return details.map((item, index) => ({
      order: (paginator?.current - 1) * paginator?.numItemsPerPage + index + 1,
      created_at: item.created_at,
      documentNumber: item?.documentNumber,
      orderCode: item?.submitNumber,
      rootOrder: item?.original_order,
      transactionType: item?.transaction_type_name,
      transactionName: item?.so_type_name,
      pttt: item?.payMethod,
      paymentMethodRoot: item?.original_payMethod,
      amountPayment: +item?.amount,
      penaltyFee: item?.penalty_amount,
      isHoliday: item?.isHoliday || false,
    }));
  }, [creditIncurredDetail?.details, creditIncurredDetail?.paginator]);

  const disabledExportExcel = !dataSource?.length;

  const handleChangeTime = () => {
    const isSameDateFrom = filterRef.current.dateFrom?.isSame(
      getValues('dateFrom'),
      'day',
    );

    const isSameDateTo = filterRef.current.dateTo?.isSame(
      getValues('dateTo') ?? getValues('dateFrom'),
      'day',
    );

    if (isSameDateFrom && isSameDateTo) return;

    setValue('page', 1);

    filterRef.current = {
      ...filterRef.current,
      dateFrom: getValues('dateFrom'),
      dateTo: getValues('dateTo') ?? getValues('dateFrom'),
      page: 1,
    };

    onFetchCreditIncurred({
      payloadData: transformFilterValues({
        ...filterRef.current,
        dateFrom: getValues('dateFrom'),
        dateTo: getValues('dateTo') ?? getValues('dateFrom'),
        page: 1,
      }),
      callback: {
        onSuccess: () => {
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  const handleChangePage = async (page: number, pageSize: number) => {
    setValue('page', page);
    setValue('limit', pageSize);

    filterRef.current = {
      ...filterRef.current,
      page,
      limit: pageSize,
    };

    onFetchCreditIncurred({
      payloadData: transformFilterValues({
        ...filterRef.current,
        page,
        limit: pageSize,
      }),
      callback: {
        onSuccess: () => {
          if (anchorPointerRef?.current) {
            const top =
              anchorPointerRef.current.getBoundingClientRect().top +
              window.scrollY -
              180;
            window.scrollTo({ top, behavior: 'smooth' });
          }
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  const handleExportExcel = async () => {
    try {
      setPageLoading(true);
      const { page, limit, ...params } = transformFilterValues(
        filterRef.current,
      );

      const res = await exportTransactionCSV(params);

      if (res?.data?.success) {
        window.location.href = res?.data?.data?.path;
      }
    } catch (error: any) {
      onOpenDialogNoti({
        type: 'ERROR',
        content:
          error instanceof AxiosError
            ? error?.response?.data?.message
            : error?.message,
        isShow: true,
        title: '',
        onCloseDialog: onCloseDialogNoti,
      });
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    onFetchCreditIncurred();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={anchorPointerRef}>
      <div className="d-flex align-items-center justify-content-between">
        <StyledRangeDatePickerContainer>
          <RangeDatePickerCustom
            placeholder={
              translations ? translations['b2b.txt.ticket-expried'] : ''
            }
            startDateInit={createDateObjectByTimezone(getValues('dateFrom'))}
            endDateInit={createDateObjectByTimezone(getValues('dateTo'))}
            onChange={([start, end]) => {
              setValue('dateFrom', start ? moment(start) : null);
              setValue('dateTo', end ? moment(end) : null);
            }}
            minDate={null}
            onCalendarClose={handleChangeTime}
          />
        </StyledRangeDatePickerContainer>

        <Button
          onClick={handleExportExcel}
          disabled={disabledExportExcel}
          style={{
            filter: `grayscale(${disabledExportExcel ? '1' : '0'})`,
            cursor: disabledExportExcel ? 'not-allowed' : 'pointer',
          }}
        >
          <ExportExcel />
          {windowSize.width >= 1280 && (
            <SmallTitle
              style={{
                margin: 0,
                color: '#fab317',
                fontSize: 14,
                width: 'max-content',
                marginLeft: 6,
                fontWeight: 400,
              }}
            >
              {translations?.['b2b.txt.export-excel']}
            </SmallTitle>
          )}
        </Button>
      </div>

      <div style={{ width: '100%', marginTop: 24 }}>
        <StyledTable
          tableRef={tableRef}
          dataSource={dataSource ?? []}
          columns={columns}
          tableProps={{
            pagination: false,
            scroll: {
              x: dataSource?.length ? 'auto' : 'max-content',
              y: dataSource?.length ? '50vh' : undefined,
            },
          }}
          styleSlots={{
            antTableContainer: {
              overflow: 'hidden',
            },
            antTableHeader: {
              border: 'none !important',
              borderRadius: '0 !important',
            },
            tableCSS: {
              borderTopLeftRadius: !dataSource?.length ? '' : '0 !important',
              borderTopRightRadius: !dataSource?.length ? '' : '0 !important',
            },
            antTableBody: {
              paddingBottom: 10,
            },
            antTableExpandedRowFixed: {
              minHeight: 110,
            },
          }}
        />
        {creditIncurredDetail?.paginator && (
          <>
            <StyledPagination
              showSizeChanger
              style={{ margin: 'auto', marginTop: 24 }}
              current={getValues('page') || 1}
              total={creditIncurredDetail?.paginator?.totalCount || 0}
              pageSize={getValues('limit') || 20}
              onChange={handleChangePage}
              pageSizeOptions={['20', '50', '100']}
            />
          </>
        )}
      </div>
    </div>
  );
};

const StyledRangeDatePickerContainer = styled(RangeDatePickerContainer)`
  @media (max-width: 768px) {
    width: calc(100% - 36px);
  }
`;
